import { useState, useEffect } from 'react';
import { Bug, RefreshCw, Copy, CheckCircle, XCircle, AlertCircle, Info, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface DiagnosticLog {
  id: string;
  timestamp: Date;
  level: 'info' | 'warn' | 'error' | 'success';
  step: string;
  message: string;
  data?: unknown;
}

// Global log storage for diagnostic panel
const diagnosticLogs: DiagnosticLog[] = [];
const logListeners: Set<() => void> = new Set();

export const addDiagnosticLog = (
  level: DiagnosticLog['level'],
  step: string,
  message: string,
  data?: unknown
) => {
  const log: DiagnosticLog = {
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date(),
    level,
    step,
    message,
    data,
  };
  
  diagnosticLogs.push(log);
  
  // Keep only last 100 logs
  if (diagnosticLogs.length > 100) {
    diagnosticLogs.shift();
  }
  
  // Notify listeners
  logListeners.forEach(listener => listener());
  
  // Also log to console with structured format
  const consoleMethod = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
  consoleMethod(`[Driver Diag] [${level.toUpperCase()}] [${step}] ${message}`, data || '');
};

export const clearDiagnosticLogs = () => {
  diagnosticLogs.length = 0;
  logListeners.forEach(listener => listener());
};

const useDiagnosticLogs = () => {
  const [logs, setLogs] = useState<DiagnosticLog[]>([...diagnosticLogs]);
  
  useEffect(() => {
    const listener = () => setLogs([...diagnosticLogs]);
    logListeners.add(listener);
    return () => { logListeners.delete(listener); };
  }, []);
  
  return logs;
};

const getLevelIcon = (level: DiagnosticLog['level']) => {
  switch (level) {
    case 'success': return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'error': return <XCircle className="h-4 w-4 text-destructive" />;
    case 'warn': return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    default: return <Info className="h-4 w-4 text-blue-500" />;
  }
};

const getLevelBadgeVariant = (level: DiagnosticLog['level']) => {
  switch (level) {
    case 'success': return 'default';
    case 'error': return 'destructive';
    case 'warn': return 'secondary';
    default: return 'outline';
  }
};

export const DriverDiagnosticPanel = () => {
  const { user, company } = useAuth();
  const logs = useDiagnosticLogs();
  const [isRunningDiagnostic, setIsRunningDiagnostic] = useState(false);
  const [sessionStatus, setSessionStatus] = useState<'unknown' | 'valid' | 'invalid' | 'checking'>('unknown');

  const runFullDiagnostic = async () => {
    setIsRunningDiagnostic(true);
    clearDiagnosticLogs();
    
    try {
      // Step 1: Check Supabase Auth
      addDiagnosticLog('info', 'AUTH_CHECK', 'Verificando autenticação Supabase...');
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        addDiagnosticLog('error', 'AUTH_CHECK', 'Erro ao obter sessão Supabase', sessionError);
      } else if (sessionData.session) {
        addDiagnosticLog('success', 'AUTH_CHECK', 'Sessão Supabase válida', {
          userId: sessionData.session.user.id,
          email: sessionData.session.user.email,
          expiresAt: sessionData.session.expires_at,
        });
      } else {
        addDiagnosticLog('warn', 'AUTH_CHECK', 'Nenhuma sessão Supabase encontrada');
      }

      // Step 2: Check localStorage token
      addDiagnosticLog('info', 'TOKEN_CHECK', 'Verificando token no localStorage...');
      const storedToken = localStorage.getItem('driver_session_token');
      
      if (storedToken) {
        addDiagnosticLog('success', 'TOKEN_CHECK', 'Token encontrado no localStorage', {
          tokenPreview: `${storedToken.substring(0, 8)}...${storedToken.substring(storedToken.length - 8)}`,
          tokenLength: storedToken.length,
        });
      } else {
        addDiagnosticLog('warn', 'TOKEN_CHECK', 'Nenhum token de sessão encontrado no localStorage');
      }

      // Step 3: Validate session in database
      if (storedToken && user) {
        addDiagnosticLog('info', 'SESSION_VALIDATE', 'Validando sessão no banco de dados...');
        setSessionStatus('checking');
        
        const startTime = Date.now();
        const { data: validateResult, error: validateError } = await supabase.rpc(
          'validate_driver_session',
          { p_session_token: storedToken }
        );
        const duration = Date.now() - startTime;
        
        if (validateError) {
          addDiagnosticLog('error', 'SESSION_VALIDATE', `Erro na validação (${duration}ms)`, validateError);
          setSessionStatus('invalid');
        } else if (validateResult) {
          addDiagnosticLog('success', 'SESSION_VALIDATE', `Sessão válida no banco (${duration}ms)`, validateResult);
          setSessionStatus('valid');
        } else {
          addDiagnosticLog('warn', 'SESSION_VALIDATE', `Sessão inválida ou expirada (${duration}ms)`);
          setSessionStatus('invalid');
        }
      }

      // Step 4: Get client info
      addDiagnosticLog('info', 'CLIENT_INFO', 'Obtendo informações do cliente...');
      try {
        const response = await supabase.functions.invoke('get-client-ip');
        if (response.error) {
          addDiagnosticLog('warn', 'CLIENT_INFO', 'Erro ao obter IP', response.error);
        } else {
          addDiagnosticLog('success', 'CLIENT_INFO', 'Informações do cliente obtidas', response.data);
        }
      } catch (err) {
        addDiagnosticLog('warn', 'CLIENT_INFO', 'Falha ao chamar edge function', err);
      }

      // Step 5: Check company context
      addDiagnosticLog('info', 'COMPANY_CHECK', 'Verificando contexto da empresa...');
      if (company) {
        addDiagnosticLog('success', 'COMPANY_CHECK', 'Empresa carregada', {
          id: company.id,
          name: company.name,
        });
      } else {
        addDiagnosticLog('warn', 'COMPANY_CHECK', 'Nenhuma empresa no contexto');
      }

      // Step 6: Check driver profile
      if (user) {
        addDiagnosticLog('info', 'PROFILE_CHECK', 'Verificando perfil do motorista...');
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id, full_name, role, active')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (profileError) {
          addDiagnosticLog('error', 'PROFILE_CHECK', 'Erro ao buscar perfil', profileError);
        } else if (profile) {
          addDiagnosticLog('success', 'PROFILE_CHECK', 'Perfil encontrado', profile);
        } else {
          addDiagnosticLog('warn', 'PROFILE_CHECK', 'Perfil não encontrado');
        }
      }

      addDiagnosticLog('success', 'DIAGNOSTIC', 'Diagnóstico completo finalizado');
    } catch (error) {
      addDiagnosticLog('error', 'DIAGNOSTIC', 'Erro durante diagnóstico', error);
    } finally {
      setIsRunningDiagnostic(false);
    }
  };

  const copyLogsToClipboard = () => {
    const logText = logs.map(log => 
      `[${log.timestamp.toISOString()}] [${log.level.toUpperCase()}] [${log.step}] ${log.message}${log.data ? `\n  Data: ${JSON.stringify(log.data, null, 2)}` : ''}`
    ).join('\n\n');
    
    navigator.clipboard.writeText(logText);
    toast.success('Logs copiados para a área de transferência');
  };

  // Only show in dev mode or with debug flag
  const showDiagnostic = import.meta.env.DEV || localStorage.getItem('driver_debug') === 'true';
  
  if (!showDiagnostic) return null;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="fixed bottom-20 right-4 z-50 rounded-full bg-muted/80 backdrop-blur-sm shadow-lg"
        >
          <Bug className="h-5 w-5 text-muted-foreground" />
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-[80vh]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5" />
            Diagnóstico do Driver
            {sessionStatus !== 'unknown' && (
              <Badge variant={sessionStatus === 'valid' ? 'default' : sessionStatus === 'invalid' ? 'destructive' : 'secondary'}>
                {sessionStatus === 'valid' ? 'Sessão OK' : sessionStatus === 'invalid' ? 'Sessão Inválida' : 'Verificando...'}
              </Badge>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Status Cards */}
          <div className="grid grid-cols-2 gap-2">
            <Card className="p-3">
              <div className="text-xs text-muted-foreground">Usuário</div>
              <div className="text-sm font-medium truncate">
                {user?.email || 'Não autenticado'}
              </div>
            </Card>
            <Card className="p-3">
              <div className="text-xs text-muted-foreground">Empresa</div>
              <div className="text-sm font-medium truncate">
                {company?.name || 'Não carregada'}
              </div>
            </Card>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button 
              onClick={runFullDiagnostic} 
              disabled={isRunningDiagnostic}
              className="flex-1"
            >
              {isRunningDiagnostic ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Executar Diagnóstico
            </Button>
            <Button variant="outline" onClick={copyLogsToClipboard} disabled={logs.length === 0}>
              <Copy className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={clearDiagnosticLogs} disabled={logs.length === 0}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          {/* Logs */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center justify-between">
                Histórico de Eventos
                <Badge variant="outline">{logs.length} logs</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[40vh]">
                {logs.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground text-sm">
                    Clique em "Executar Diagnóstico" para iniciar
                  </div>
                ) : (
                  <div className="divide-y">
                    {logs.slice().reverse().map((log) => (
                      <div key={log.id} className="p-3 text-sm">
                        <div className="flex items-start gap-2">
                          {getLevelIcon(log.level)}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant={getLevelBadgeVariant(log.level)} className="text-xs">
                                {log.step}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {log.timestamp.toLocaleTimeString()}
                              </span>
                            </div>
                            <p className="mt-1 text-foreground">{log.message}</p>
                            {log.data && (
                              <pre className="mt-1 text-xs bg-muted p-2 rounded overflow-x-auto">
                                {JSON.stringify(log.data, null, 2)}
                              </pre>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </SheetContent>
    </Sheet>
  );
};
